// @ts-check
import { decamelizeKeys } from 'humps';
import { getAdyenTestResponseCode } from 'utils/adyen';

/**
 * Interface for Adyen Google Pay strategy.
 * @typedef {Object} AdyenGooglePayStrategy
 * @property {function(Object): Promise<Object>} createPayload - Create payment payload.
 */

/**
 * Adyen Google Pay strategy.
 * @function AdyenGooglePayStrategy
 * @returns {AdyenGooglePayStrategy}
 */
const AdyenGooglePayStrategy = () => {
  /**
   * Create a payload for Adyen Google Pay.
   * Unlike Apple Pay, Google Pay requires browser_info for potential 3DS on FPAN tokens.
   * browser_info is sent as a top-level field with snake_case keys.
   * @param {Object} googlePayData - The data received from Google Pay component (state.data from onSubmit).
   * @param {Object} googlePayData.paymentMethod - Payment method object from Adyen.
   * @param {string} googlePayData.paymentMethod.type - Payment method type ('paywithgoogle').
   * @param {string} googlePayData.paymentMethod.googlePayToken - Google Pay token string.
   * @param {string} googlePayData.paymentMethod.googlePayCardNetwork - Card network used (e.g., 'VISA').
   * @param {Object} googlePayData.browserInfo - Browser info from Adyen SDK (camelCase keys).
   * @returns {Promise<Object>} The payload formatted for the backend.
   */
  const createPayload = async (googlePayData) => {
    const { paymentMethod, browserInfo } = googlePayData;
    const payload = {
      google_pay_data: {
        paymentMethod,
      },
      browser_info: decamelizeKeys(browserInfo),
    };

    const testResponseCode = getAdyenTestResponseCode();
    if (testResponseCode) {
      payload.additional_data = {
        RequestedTestAcquirerResponseCode: testResponseCode,
      };
    }

    return payload;
  };

  return { createPayload };
};

const adyenGooglePayStrategy = AdyenGooglePayStrategy();

export default adyenGooglePayStrategy;
